import clsx from 'clsx';

import * as api from '@gitbook/api-client';
import { DateShortRelative } from '@gitbook/react-uikit';

import {
    ChangeRequestDescriptionField,
    ChangeRequestStatusBadge,
    ChangeRequestSubject,
    ChangeRequestUnarchiveButton,
    UserProfilePrettyName,
} from '../../../Components';
import { ChangeRequestDetailsResponsive } from './ChangeRequestDetails';
import {
    ChangeRequestMergeBlock,
    type ChangeRequestMergeBlockProps,
} from './ChangeRequestMergeBlock';

export type ChangeRequestIntroProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    className?: string;
} & ChangeRequestMergeBlockProps;

/**
 * Intro presenting the change requests: its subject, description, status, etc.
 * With key actions to merge and review.
 */
export function ChangeRequestIntro(props: ChangeRequestIntroProps) {
    const { space, changeRequest, analyticsContext, className } = props;

    return (
        // biome-ignore lint/nursery/useUniqueElementIds: I want a static ID here
        <div id="change-request-intro" className={clsx('flex flex-col gap-2 py-4', className)}>
            <div className="flex flex-row items-center gap-2 pr-6 pl-4">
                <ChangeRequestSubject
                    space={space}
                    changeRequest={changeRequest}
                    size="large"
                    editable
                />
                <span className="shrink-0 font-light text-muted text-ui-heading-large">
                    #{changeRequest.number}
                </span>
            </div>
            <div className="flex flex-col px-6">
                <div className="mb-4 flex flex-row items-center gap-2">
                    <ChangeRequestStatusBadge status={changeRequest.status} size="small" />
                    <div className="text-secondary">
                        Created{' '}
                        <DateShortRelative value={changeRequest.createdAt} onlyDate={false} /> by{' '}
                        <UserProfilePrettyName user={changeRequest.createdBy} />
                    </div>
                </div>

                <ChangeRequestDetailsResponsive changeRequest={changeRequest} space={space} />

                <div className="flex flex-col gap-6">
                    <ChangeRequestDescriptionField changeRequest={changeRequest} />
                    <div className="flex flex-col gap-4 empty:hidden">
                        {changeRequest.status === api.ChangeRequestStatus.Open ||
                        changeRequest.status === api.ChangeRequestStatus.Draft ? (
                            <ChangeRequestMergeBlock
                                space={space}
                                changeRequest={changeRequest}
                                analyticsContext={analyticsContext}
                            />
                        ) : null}
                        {changeRequest.status === api.ChangeRequestStatus.Archived ? (
                            <ChangeRequestUnarchiveButton
                                space={space}
                                changeRequest={changeRequest}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
